import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
    Container,
    Typography,
    Grid,
    TextField,
    Button,
    Box,
    Paper,
    Divider,
    Alert,
    Snackbar,
    FormControlLabel,
    Checkbox,
    MenuItem,
    CircularProgress,
    IconButton
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Send as SendIcon,
    Business as BusinessIcon,
    AccessTime as AccessTimeIcon,
    Engineering as EngineeringIcon,
    Construction as ConstructionIcon,
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import '../styles/Contact.scss';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        position: '',
        projectType: '',
        projectLocation: '',
        budget: '',
        startDate: '',
        priority: 'normal',
        preferredContact: 'email',
        message: '',
        newsletter: false,
        gdpr: false
    });

    const [attachments, setAttachments] = useState([]);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [activeSection, setActiveSection] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [fileError, setFileError] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = [
            'image/jpeg',
            'image/png',
            'image/gif',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ];

        // Ellenőrizzük, hogy nem léptük-e túl a maximum fájlszámot
        if (attachments.length + files.length > 5) {
            setFileError('Maximum 5 fájl tölthető fel.');
            return;
        }

        // Ellenőrizzük a fájlok méretét és típusát
        const invalidFiles = files.filter(file => {
            if (file.size > maxSize) {
                setFileError(`A ${file.name} mérete nagyobb mint 5MB.`);
                return true;
            }
            if (!allowedTypes.includes(file.type)) {
                setFileError(`A ${file.name} típusa nem támogatott.`);
                return true;
            }
            return false;
        });

        if (invalidFiles.length > 0) {
            return;
        }

        setFileError(null);
        setAttachments(prev => [...prev, ...files]);
    };

    const handleFileDelete = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
        setFileError(null);
    };

    const getFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) return '🖼️';
        if (fileType.includes('pdf')) return '📄';
        if (fileType.includes('word')) return '📝';
        if (fileType.includes('excel')) return '📊';
        return '📎';
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            const formDataToSend = new FormData();

            // Alap adatok hozzáadása
            Object.keys(formData).forEach(key => {
                formDataToSend.append(key, formData[key]);
            });

            // Fájlok hozzáadása
            attachments.forEach(file => {
                formDataToSend.append('attachments', file);
            });

    const API_URL = process.env.REACT_APP_API_URL;

            const response = await fetch(`${API_URL}/api/contact`, {
                method: 'POST',
                body: formDataToSend
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Hiba történt a küldés során. Kérjük, próbálja újra később.');
            }

            const result = await response.json();

            setSubmitStatus('success');
            setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                position: '',
                projectType: '',
                projectLocation: '',
                budget: '',
                startDate: '',
                priority: 'normal',
                preferredContact: 'email',
                message: '',
                newsletter: false,
                gdpr: false
            });
            setAttachments([]);
        } catch (err) {
            setError(err.message);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: <BusinessIcon />,
            title: 'Cégnév',
            content: 'Toronyszerver KFT.',
            link: 'https://www.nemzeticegtar.hu/nemzeticegtar/cegadat/1309160535/Toronyszerver-Kft',
            delay: 0.2
        },
        {
            icon: <PhoneIcon />,
            title: 'Telefon',
            content: '+36 70 618 5311',
            link: 'tel:+36706185311',
            delay: 0.3
        },
        {
            icon: <EmailIcon />,
            title: 'Email',
            content: 'iroda@toronyszerver.hu',
            link: 'mailto:iroda@toronyszerver.hu',
            delay: 0.4
        },
        {
            icon: <LocationIcon />,
            title: 'Székhely',
            content: '2230 Gyömrő, Állomás utca 15.',
            link: 'https://www.google.com/maps?q=Gyömrő,+Állomás+u.+15',
            delay: 0.5
        }
    ];

    const features = [
        {
            icon: <EngineeringIcon />,
            title: 'Szakértő Csapat',
            description: 'Több mint 10 éves tapasztalat az építőiparban',
        },
        {
            icon: <AccessTimeIcon />,
            title: 'Gyors Válasz',
            description: '48 órán belüli visszajelzés',
        },
        {
            icon: <ConstructionIcon />,
            title: 'Professzionális Kivitelezés',
            description: 'Minőségi munka, megbízható határidőkkel',
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    return (
        <div className="contact-page">
            <motion.div
                className="page-header"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="header-background" />
                <Container>
                    <motion.div
                        initial={{ y: -30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                        className="header-content"
                    >
                        <Typography variant="h1">Kapcsolat</Typography>
                        <Typography variant="subtitle1">
                            Szakértő csapatunk készen áll, hogy segítsen megvalósítani építőipari projektjeit
                        </Typography>
                    </motion.div>
                </Container>
            </motion.div>

            <Container className="contact-container">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="features-section"
                >
                    <Grid container spacing={3}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <motion.div variants={itemVariants}>
                                    <Paper elevation={3} className="feature-card">
                                        <Box className="feature-icon">
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h6">{feature.title}</Typography>
                                        <Typography variant="body2">{feature.description}</Typography>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>

                <Grid container spacing={4} className="main-content">
                    <Grid item xs={12} md={5}>
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                        >
                            <Paper elevation={3} className="contact-info-section">
                                <Typography variant="h5" className="section-title">
                                    Elérhetőségeink
                                </Typography>
                                <Box className="contact-info-list">
                                    {contactInfo.map((info, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ x: -20, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            transition={{ delay: info.delay }}
                                        >
                                            <Box
                                                className="contact-info-item"
                                                component={info.link ? 'a' : 'div'}
                                                href={info.link}
                                                target={info.link ? "_blank" : undefined}
                                                rel={info.link ? "noopener noreferrer" : undefined}
                                            >
                                                <Box className="icon-container">
                                                    {info.icon}
                                                </Box>
                                                <Box className="info-content">
                                                    <Typography variant="subtitle2">{info.title}</Typography>
                                                    <Typography variant="body1">{info.content}</Typography>
                                                </Box>
                                            </Box>
                                        </motion.div>
                                    ))}
                                </Box>
                            </Paper>

                            <Paper elevation={3} className="map-section">
                                <div className="map-overlay">
                                    <Typography variant="subtitle1">Központi Irodánk</Typography>
                                </div>
                                <iframe
                                    title="Toronyszerver KFT location"
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2696.825544366374!2d19.385472776892176!3d47.43246777107242!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741bf1d0507fecf%3A0x8172b7600a76ea48!2zR3nDtm1yxZEsIMOBbGxvbcOhcyB1LiAxNSwgMjIzMA!5e0!3m2!1shu!2shu!4v1706624156447!5m2!1shu!2shu"
                                    width="100%"
                                    height="300"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </Paper>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5, duration: 0.6 }}
                        >
                            <Paper elevation={3} className="contact-form-section">
                                <Typography variant="h5" className="section-title">
                                    Kapcsolatfelvétel
                                </Typography>
                                <Typography variant="body2" className="form-subtitle">
                                    Töltse ki az alábbi űrlapot és kollégánk 24 órán belül felveszi Önnel a kapcsolatot
                                </Typography>
                                <form onSubmit={handleSubmit} className="contact-form">
                                    <Box mb={4}>
                                        <Typography variant="h6" color="primary" gutterBottom>
                                            Személyes Információk
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Név"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Email cím"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Telefonszám"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Cég neve"
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Beosztás"
                                                    name="position"
                                                    value={formData.position}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Preferált kapcsolattartás"
                                                    name="preferredContact"
                                                    value={formData.preferredContact}
                                                    onChange={handleChange}
                                                    select
                                                    variant="outlined"
                                                >
                                                    <MenuItem value="email">Email</MenuItem>
                                                    <MenuItem value="phone">Telefon</MenuItem>
                                                    <MenuItem value="both">Mindkettő</MenuItem>
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box mb={4}>
                                        <Typography variant="h6" color="primary" gutterBottom>
                                            Projekt Részletek
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Projekt típusa"
                                                    name="projectType"
                                                    value={formData.projectType}
                                                    onChange={handleChange}
                                                    select
                                                    required
                                                    variant="outlined"
                                                >
                                                    <MenuItem value="">Válasszon...</MenuItem>
                                                    <MenuItem value="industrial">Ipari létesítmény</MenuItem>
                                                    <MenuItem value="commercial">Kereskedelmi ingatlan</MenuItem>
                                                    <MenuItem value="tower">Toronyépítés</MenuItem>
                                                    <MenuItem value="renovation">Felújítás</MenuItem>
                                                    <MenuItem value="infrastructure">Infrastruktúra</MenuItem>
                                                    <MenuItem value="other">Egyéb</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Projekt helyszíne"
                                                    name="projectLocation"
                                                    value={formData.projectLocation}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Tervezett költségkeret"
                                                    name="budget"
                                                    value={formData.budget}
                                                    onChange={handleChange}
                                                    select
                                                    variant="outlined"
                                                >
                                                    <MenuItem value="">Válasszon...</MenuItem>
                                                    <MenuItem value="10m_under">10M Ft alatt</MenuItem>
                                                    <MenuItem value="10m_50m">10M - 50M Ft</MenuItem>
                                                    <MenuItem value="50m_100m">50M - 100M Ft</MenuItem>
                                                    <MenuItem value="100m_above">100M Ft felett</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Tervezett kezdés"
                                                    name="startDate"
                                                    type="date"
                                                    value={formData.startDate}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Prioritás"
                                                    name="priority"
                                                    value={formData.priority}
                                                    onChange={handleChange}
                                                    select
                                                    variant="outlined"
                                                >
                                                    <MenuItem value="low">Alacsony</MenuItem>
                                                    <MenuItem value="normal">Normál</MenuItem>
                                                    <MenuItem value="high">Sürgős</MenuItem>
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box mb={4}>
                                        <Typography variant="h6" color="primary" gutterBottom>
                                            Projekt Leírás
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Részletes leírás"
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                    multiline
                                                    rows={6}
                                                    variant="outlined"
                                                    placeholder="Kérjük, írja le részletesen a projekt követelményeit, speciális igényeit és egyéb fontos információkat..."
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box sx={{ mt: 3 }}>
                                                    <Typography variant="subtitle1" gutterBottom>
                                                        Csatolmányok
                                                    </Typography>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Button
                                                            component="label"
                                                            variant="outlined"
                                                            startIcon={<CloudUploadIcon />}
                                                            disabled={attachments.length >= 5}
                                                        >
                                                            Fájl feltöltése
                                                            <VisuallyHiddenInput
                                                                type="file"
                                                                onChange={handleFileChange}
                                                                multiple
                                                                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                                                            />
                                                        </Button>
                                                    </Box>
                                                    {fileError && (
                                                        <Alert severity="error" sx={{ mb: 2 }}>
                                                            {fileError}
                                                        </Alert>
                                                    )}
                                                    {attachments.length > 0 && (
                                                        <Paper variant="outlined" sx={{ p: 2 }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Feltöltött fájlok ({attachments.length}/5):
                                                            </Typography>
                                                            {attachments.map((file, index) => (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        p: 1,
                                                                        mb: 1,
                                                                        borderRadius: 1,
                                                                        bgcolor: 'background.default',
                                                                        '&:last-child': { mb: 0 }
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                                                                        <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                                                                            {getFileIcon(file.type)}
                                                                        </Typography>
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                            <Typography variant="body2" noWrap title={file.name}>
                                                                                {file.name}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {formatFileSize(file.size)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleFileDelete(index)}
                                                                        sx={{ color: 'error.main' }}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Box>
                                                            ))}
                                                        </Paper>
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box mb={4}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.newsletter}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        newsletter: e.target.checked
                                                    })}
                                                    color="primary"
                                                />
                                            }
                                            label="Szeretnék értesülni a Toronyszerver KFT. hírleveleiről és ajánlatairól"
                                        />
                                        <FormControlLabel
                                            required
                                            control={
                                                <Checkbox
                                                    checked={formData.gdpr}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        gdpr: e.target.checked
                                                    })}
                                                    color="primary"
                                                />
                                            }
                                            label="Elfogadom az adatkezelési tájékoztatót"
                                        />
                                    </Box>

                                    <motion.div
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            endIcon={!isSubmitting && <SendIcon />}
                                            fullWidth
                                            className="submit-button"
                                            disabled={!formData.gdpr || isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Üzenet küldése'
                                            )}
                                        </Button>
                                    </motion.div>
                                </form>
                            </Paper>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            <Snackbar
                open={submitStatus === 'success' || submitStatus === 'error'}
                autoHideDuration={6000}
                onClose={() => setSubmitStatus(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    bottom: { xs: 0, sm: 24 },
                    width: { xs: '100%', sm: 'auto' }
                }}
            >
                <Alert
                    severity={submitStatus === 'success' ? 'success' : 'error'}
                    variant="filled"
                    onClose={() => setSubmitStatus(null)}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: '400px' },
                        borderRadius: { xs: '0', sm: '12px' },
                        backgroundColor: submitStatus === 'success' ? '#2c5049' : '#d32f2f',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                        padding: '16px 24px',
                        '& .MuiAlert-icon': {
                            fontSize: '28px',
                            opacity: 1,
                            color: submitStatus === 'success' ? '#4CAF50' : '#ff5252'
                        },
                        '& .MuiAlert-message': {
                            fontSize: '1rem',
                            padding: '8px 0',
                            color: 'white'
                        },
                        '& .MuiAlert-action': {
                            padding: '0 0 0 16px',
                            '& .MuiButtonBase-root': {
                                color: 'rgba(255, 255, 255, 0.8)',
                                padding: '8px',
                                '&:hover': {
                                    color: 'white'
                                }
                            }
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: 'inherit',
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))',
                            pointerEvents: 'none'
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                                {submitStatus === 'success' ? 'Sikeres küldés!' : 'Hiba történt!'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                {submitStatus === 'success'
                                    ? 'Köszönjük megkeresését! Kollégánk 24 órán belül felveszi Önnel a kapcsolatot.'
                                    : error || 'Hiba történt a küldés során. Kérjük, próbálja újra később.'}
                            </Typography>
                        </Box>
                    </Box>
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Contact; 